// Google Analytics 4 via Firebase.
//
// La config Firebase web n'est PAS un secret : ces identifiants sont conçus
// pour être embarqués dans le bundle client (comme VITE_API_URL). La sécurité
// se fait côté règles Firebase, pas en cachant ces clés.
//
// L'analytics est "best-effort" : toute erreur d'init/log est avalée pour ne
// JAMAIS casser l'application.
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  type Analytics,
} from 'firebase/analytics';

const firebaseConfig = {
  apiKey: 'AIzaSyBmfdhoxYPzHxY_zZCC-uhnKkG1AFvDehg',
  authDomain: 'exam-copilot-742cf.firebaseapp.com',
  projectId: 'exam-copilot-742cf',
  storageBucket: 'exam-copilot-742cf.firebasestorage.app',
  messagingSenderId: '729363435237',
  appId: '1:729363435237:web:f13be22c8bc30b2e028e0c',
  measurementId: 'G-L6K3G4GPZ9',
};

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let ready = false;

/**
 * Initialise Firebase Analytics une seule fois, uniquement dans un navigateur
 * qui le supporte (isSupported écarte SSR, certains in-app browsers, etc.).
 * GA4 envoie automatiquement le premier `page_view` au chargement ; les
 * changements de route SPA sont suivis via trackPageView().
 */
export async function initAnalytics(): Promise<void> {
  if (ready || typeof window === 'undefined') return;
  ready = true;
  try {
    if (!(await isSupported())) return;
    app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
  } catch {
    // best-effort : on n'interrompt jamais l'app pour de l'analytics
  }
}

/** Log générique d'un événement GA4. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!analytics) return;
  try {
    logEvent(analytics, name as string, params as Record<string, unknown>);
  } catch {
    /* noop */
  }
}

/** Suivi d'une vue de page (changement de route dans le SPA). */
export function trackPageView(path: string, title?: string): void {
  if (!analytics || typeof window === 'undefined') return;
  trackEvent('page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

/**
 * Catégorie d'appareil (mobile / tablet / desktop). GA4 la collecte déjà comme
 * dimension automatique, mais on l'ajoute aussi en PARAMÈTRE des événements du
 * tunnel d'achat pour pouvoir segmenter le parcours (view_item → purchase) par
 * type d'appareil directement dans une exploration d'événements.
 */
export function deviceCategory(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod|IEMobile|BlackBerry|Opera Mini/i.test(ua)) {
    return 'mobile';
  }
  // Filet de sécurité pour les tablettes tactiles modernes non détectées par l'UA.
  return window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop';
}

/** Associe (ou dissocie) l'utilisateur connecté aux événements. */
export function setAnalyticsUser(id: string | null): void {
  if (!analytics) return;
  try {
    setUserId(analytics, id as string);
  } catch {
    /* noop */
  }
}
